import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FinancePnlWindow } from '@nadoutong/contracts';
import { LocalDateTimeInput } from '../../components/LocalDateTimeInput.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import type { BrandFilter } from '../../api/revenue.ts';
import { Button, PageHeader, Select, Tabs, TabsList, TabsTrigger } from '../../ui/index.ts';
import type { SelectOption } from '../../ui/index.ts';
import {
  DEFAULT_METRICS_TAB,
  METRICS_TABS,
  METRICS_WINDOWS,
  PNL_WINDOWS,
  parseBrand,
  parseMetricsTab,
  parsePnlWindow,
  narrowWindow,
  windowRange,
} from './hubState.ts';
import type { MetricsTab, MetricsTabContext } from './hubState.ts';
import { ModelsTab } from './ModelsTab.tsx';
import { OverviewTab } from './OverviewTab.tsx';
import { PlatformSkuTab } from './PlatformSkuTab.tsx';
import { PnlTab } from './PnlTab.tsx';
import { SkuTab } from './SkuTab.tsx';
import { TenantsTab } from './TenantsTab.tsx';
import { UpstreamTab } from './UpstreamTab.tsx';
import { UsageTab } from './UsageTab.tsx';

/**
 * 计量计价中心 — the consolidated admin metrics hub at
 * `/business/metrics` (epic #1643; per-group tabs #1675). Eight
 * URL-backed tabs share one
 * filter row (window pills + brand). S5 absorbed the standalone
 * usage/pnl/sku pages into their tabs; the pnl tab serves the full
 * `FinancePnlWindow` set, so the pill row shows the extended presets
 * while it is active and every other tab narrows those via
 * `narrowWindow`.
 */

const TAB_LABEL_KEYS: Record<MetricsTab, I18nKey> = {
  overview: 'metrics.tab.overview',
  models: 'metrics.tab.models',
  platform_sku: 'metrics.tab.platform_sku',
  upstream: 'metrics.tab.upstream',
  tenants: 'metrics.tab.tenants',
  usage: 'metrics.tab.usage',
  pnl: 'metrics.tab.pnl',
  sku: 'metrics.tab.sku',
};

/** Covers the full pnl set; the shared four reuse the hub's own labels,
 * the pnl-only extras reuse the established `finance.pnl.window.*` keys. */
const WINDOW_LABEL_KEYS: Record<FinancePnlWindow, I18nKey> = {
  today: 'metrics.window.today',
  '7d': 'metrics.window.7d',
  '30d': 'metrics.window.30d',
  custom: 'metrics.window.custom',
  this_month: 'finance.pnl.window.this_month',
  last_month: 'finance.pnl.window.last_month',
  all: 'finance.pnl.window.all',
};

/**
 * Radix Select forbids empty-string item values, so "全部" travels as
 * the `'all'` sentinel; URL semantics keep `''` = no brand param.
 */
const BRAND_SENTINEL_ALL = 'all';

const DAY_MS = 24 * 60 * 60 * 1000;

export function MetricsHubPage(): JSX.Element {
  const t = useT();
  const [params, setParams] = useSearchParams();
  const tab = parseMetricsTab(params.get('tab'));
  // The pnl tab accepts this_month/last_month/all in `?window=`; every
  // other tab narrows those to a fixed window it can actually serve.
  const window = narrowWindow(params.get('window'));
  // The pill row follows the ACTIVE tab: full FinancePnlWindow set on
  // pnl (raw param value), the shared four (narrowed) everywhere else.
  const pnlWindow = parsePnlWindow(params.get('window'));
  const windowOptions: readonly FinancePnlWindow[] = tab === 'pnl' ? PNL_WINDOWS : METRICS_WINDOWS;
  const activeWindow: FinancePnlWindow = tab === 'pnl' ? pnlWindow : window;
  const brand = parseBrand(params.get('brand'));
  // Stable defaults for the `custom` bounds (FinancePnlPage idiom):
  // seeded into the URL when the operator picks `custom`, and used as a
  // query fallback for a hand-crafted `?window=custom` URL that omits
  // from/to — a custom request without bounds would 400 server-side
  // ("custom requires both"), so the queries must never send one.
  const customDefaults = useMemo(() => ({ from: defaultCustomFrom(), to: defaultCustomTo() }), []);
  const customFrom = params.get('from') ?? customDefaults.from;
  const customTo = params.get('to') ?? customDefaults.to;
  const range = useMemo(
    () => windowRange(window, customFrom, customTo),
    [window, customFrom, customTo],
  );
  const context: MetricsTabContext = { window, brand, customFrom, customTo, range };

  function selectTab(value: string): void {
    const match = parseMetricsTab(value);
    const next = new URLSearchParams(params);
    if (match === DEFAULT_METRICS_TAB) {
      next.delete('tab');
    } else {
      next.set('tab', match);
    }
    setParams(next, { replace: true });
  }

  function setWindow(next: FinancePnlWindow): void {
    const clone = new URLSearchParams(params);
    clone.set('window', next);
    // Seed the bounds so the URL is immediately shareable and the
    // queries have concrete `[from, to)` values from the first render.
    // Bounds are KEPT across window toggles (FinancePnlPage idiom,
    // #1008): they only affect the queries while `custom` is active, so
    // a custom → preset → custom round-trip restores the operator's
    // last-edited range instead of resetting it.
    if (next === 'custom') {
      if (clone.get('from') === null) clone.set('from', customDefaults.from);
      if (clone.get('to') === null) clone.set('to', customDefaults.to);
    }
    setParams(clone, { replace: true });
  }

  function setCustomBound(key: 'from' | 'to', value: string): void {
    const clone = new URLSearchParams(params);
    clone.set('window', 'custom');
    if (value.length > 0) clone.set(key, value);
    else clone.delete(key);
    setParams(clone, { replace: true });
  }

  function setBrand(next: BrandFilter): void {
    const clone = new URLSearchParams(params);
    if (next.length > 0) clone.set('brand', next);
    else clone.delete('brand');
    setParams(clone, { replace: true });
  }

  const brandOptions: readonly SelectOption[] = [
    { value: BRAND_SENTINEL_ALL, label: t('metrics.brand.all') },
    { value: 'nadoutong', label: 'Nadoutong' },
    { value: 'nxtpath', label: 'Nxtpath' },
  ];

  return (
    <section className="flex flex-col gap-6" data-testid="metrics-hub-page">
      <PageHeader title={t('metrics.title')} sub={t('metrics.subtitle')} />

      <div className="flex flex-wrap items-end gap-3">
        <div
          role="tablist"
          aria-label={t('metrics.window.aria')}
          className="inline-flex w-fit flex-wrap gap-1 rounded-lg border border-line-soft bg-card p-1"
        >
          {windowOptions.map((option) => {
            const active = option === activeWindow;
            return (
              <Button
                key={option}
                role="tab"
                aria-selected={active}
                variant={active ? 'primary' : 'ghost'}
                size="sm"
                data-testid={`metrics-window-${option}`}
                onClick={() => setWindow(option)}
              >
                {t(WINDOW_LABEL_KEYS[option])}
              </Button>
            );
          })}
        </div>
        {activeWindow === 'custom' ? (
          <>
            <LocalDateTimeInput
              label={t('metrics.custom.from')}
              value={customFrom}
              onChange={(value) => setCustomBound('from', value)}
              inputClassName="h-9 w-48"
              testId="metrics-custom-from"
            />
            <LocalDateTimeInput
              label={t('metrics.custom.to')}
              value={customTo}
              onChange={(value) => setCustomBound('to', value)}
              inputClassName="h-9 w-48"
              testId="metrics-custom-to"
            />
          </>
        ) : null}
        <div className="flex w-full max-w-xs flex-col gap-1.5">
          <span className="text-label text-paper-mid">{t('metrics.brand')}</span>
          <Select
            value={brand === '' ? BRAND_SENTINEL_ALL : brand}
            onValueChange={(value) => setBrand(parseBrand(value))}
            options={brandOptions}
            ariaLabel={t('metrics.brand')}
            testId="metrics-brand-filter"
          />
        </div>
      </div>

      <Tabs value={tab} onValueChange={selectTab}>
        <TabsList ariaLabel={t('metrics.tabs.aria')}>
          {METRICS_TABS.map((item) => (
            <TabsTrigger key={item} value={item} testId={`metrics-tab-${item}`}>
              {t(TAB_LABEL_KEYS[item])}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {tab === 'overview' ? <OverviewTab context={context} /> : null}
      {tab === 'models' ? <ModelsTab context={context} /> : null}
      {tab === 'platform_sku' ? <PlatformSkuTab context={context} /> : null}
      {tab === 'upstream' ? <UpstreamTab context={context} /> : null}
      {tab === 'tenants' ? <TenantsTab context={context} /> : null}
      {tab === 'usage' ? <UsageTab context={context} /> : null}
      {tab === 'pnl' ? <PnlTab context={context} /> : null}
      {tab === 'sku' ? <SkuTab context={context} /> : null}
    </section>
  );
}

/** Custom-window seed: the last 7 days ending now (mirrors the 7d default). */
function defaultCustomFrom(): string {
  return new Date(Date.now() - 7 * DAY_MS).toISOString();
}

function defaultCustomTo(): string {
  return new Date().toISOString();
}
